<template>
  <div>
    <tool-bar title="Search Service Logs"></tool-bar>
    <v-container
      class="page-container"
      :class="{ 'mobile pa-4': isMobile, 'pa-5': isTablet, 'py-5': isDesktop}"
      grid-list-md>
      <v-layout row wrap :class="{'pb-4': isMobile}">
        <!--no selected provider-->
        <v-flex xs12 sm6 md4 class="py-0">
          <v-combobox
            dense
            label="Find Provider"
            placeholder="Enter Provider"
            :items="providerSearchArray"
            v-model="providerSearchModel"
            :clearable="true"
            :error-messages="providerSearchError"
            :return-object="true"
            :search-input.sync="providerSearchInput"
            item-text="userId"
            :loading="providerSearchLoading"
            @focus="clearProviderAutoComplete"
            :no-filter="true">
            <!--this is how the data in the select appears-->
            <template slot="item" slot-scope="{item, tile}">
              {{item.firstName}} {{item.lastName}}
              <!--add credentials if they exist-->
              <span v-if="item.credentialName">/{{item.credentialName}}</span>
            </template>
            <template slot="selection" slot-scope="{item, selected}">
              {{item.firstName}} {{item.lastName}}
              <!--add credentials if they exist-->
              <span v-if="item.credentialName">/{{item.credentialName}}</span>
            </template>
            <!--message that displays when search matches nothing-->
            <template slot="no-data">
              <v-list>
                <!--message when no input is entered-->
                <v-list-tile v-if="!providerSearchInput">
                  Please type a Provider.
                </v-list-tile>
                <!--message when no provider matches text-->
                <v-list-tile
                  v-if="providerSearchInput && !providerSearchLoading">
                  No Results
                </v-list-tile>
              </v-list>
            </template>
          </v-combobox>
        </v-flex>
        <!--find student combobox-->
        <v-flex xs12 sm6 md4 class="py-0">
          <v-combobox
            dense
            label="Find Student"
            placeholder="Enter Student"
            :items="studentSearchArray"
            v-model="studentSearchModel"
            :clearable="true"
            :error-messages="studentSearchError"
            :return-object="true"
            :search-input.sync="studentSearchInput"
            item-text="studentName"
            item-value="studentName"
            :loading="studentSearchLoading"
            :no-filter="true"
            @focus="clearStudentAutoComplete">
            <!--this is how the data in the select appears-->
            <template slot="item" slot-scope="{item, tile}">
              <v-list-tile-content>
                <v-list-tile-title>
                  {{item.firstName}} {{item.lastName}}
                  <span class="pl-1">{{item.studentExtId}}</span>
                </v-list-tile-title>
                <v-list-tile-sub-title>
                  {{item.schoolName}}
                  <span class="pl-1">Grade: {{item.grade}}</span>
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
            <template slot="selection" slot-scope="{item, selected}">
              <v-list-tile-content>
                <v-list-tile-title>
                  {{item.firstName}} {{item.lastName}}
                </v-list-tile-title>
              </v-list-tile-content>
            </template>
            <!--message that displays when search matches nothing-->
            <template slot="no-data">
              <v-list>
                <!--message when no input is entered-->
                <v-list-tile v-if="!studentSearchInput">
                  Please type a Student.
                </v-list-tile>
                <!--message when no provider matches text-->
                <v-list-tile
                  v-if="studentSearchInput && !studentSearchLoading">
                  No Results
                </v-list-tile>
              </v-list>
            </template>
          </v-combobox>
        </v-flex>
        <!--location-->
        <v-flex class="py-0" xs12 sm4 md4>
          <v-combobox
            dense
            label="Service Location"
            placeholder="Enter Location"
            :items="locationSearchArray"
            v-model="locationSearchModel"
            :clearable="true"
            :error-messages="locationSearchError"
            item-text="locationName"
            :return-object="true"
            :search-input.sync="locationSearchInput"
            :loading="locationSearchLoading"
            :no-filter="true"
            @focus="clearLocationAutoComplete">
            <template slot="no-data">
              <v-list>
                <v-list-tile v-if="!locationSearchInput">
                  Please type a Service Location.
                </v-list-tile>
                <v-list-tile
                  v-if="locationSearchInput && !locationSearchLoading">
                  No Results
                </v-list-tile>
              </v-list>
            </template>
          </v-combobox>
        </v-flex>
        <!--service date start-->
        <v-flex xs6 sm4 md4 class="py-0">
          <v-menu
            ref="serviceMenuStart"
            :close-on-content-click="true"
            v-model="serviceMenuStart"
            :nudge-right="40"
            :return-value.sync="serviceDateStart"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            max-width="290px"
            min-width="290px">
            <v-text-field
              slot="activator"
              v-model="dateStart"
              :clearable="true"
              label="Start Date"
              placeholder="Select Start Date"
              readonly>
            </v-text-field>
            <v-date-picker
              v-model="serviceDateStart"
              no-title
              scrollable
              @change="serviceDateStartChanged(serviceDateStart)"
              @input="$refs.serviceMenuStart.save(serviceDateStart)">
            </v-date-picker>
          </v-menu>
        </v-flex>
        <!--end date-->
        <v-flex xs6 sm4 md4 class="py-0">
          <v-menu
            ref="serviceMenuEnd"
            :close-on-content-click="true"
            v-model="serviceMenuEnd"
            :nudge-right="40"
            :return-value.sync="serviceDateEnd"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            max-width="290px"
            min-width="290px">
            <v-text-field
              slot="activator"
              v-model="dateEnd"
              :clearable="true"
              label="End Date"
              placeholder="Select End Date"
              readonly>
            </v-text-field>
            <v-date-picker
              v-model="serviceDateEnd"
              no-title
              scrollable
              @change="serviceEndDateChanged(serviceDateEnd)"
              @input="$refs.serviceMenuEnd.save(serviceDateEnd)">
            </v-date-picker>
          </v-menu>
        </v-flex>
        <!--Search Button-->
        <v-flex xs12 sm6 md2 class="py-0" :class="{'mt-2': isDesktop}">
          <v-btn color="secondary" block @click="onRetrieveLogsClicked">
            Retrieve Logs
          </v-btn>
        </v-flex>
        <!--clear logs button-->
        <v-flex xs12 sm6 md2 class="py-0" :class="{'mt-2': isDesktop}">
          <v-btn
            class="clear-parameters-button"
            block
            @click="clearSearch">
            Clear Parameters
          </v-btn>
        </v-flex>
        <!--service logs-->
        <v-toolbar class="service-logs-toolbar" :class="$mq" color="primary" dark>
          <v-toolbar-title class="white--text">
            Service Logs
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <span
            v-if="totalCount && !showLoading"
            class="white--text font-italic pr-3">
              {{totalCount}} results
            </span>
          <div
            v-if="serviceLogsArray.length"
            class="items-per-page-dropdown">
            <select
              v-model="itemsPerPage"
              v-on:change="retrieveLogs">
              <option
                v-for="(num, i) in itemsPerPageArray"
                :key="i">
                {{num}}
              </option>
            </select>
          </div>
        </v-toolbar>
        <div
          v-for="(log, index) in serviceLogsArray"
          class="service-log-container"
          :key="index">
          <history-repeatable
            :submission="log"
            :loadData="serviceLogsArray"
            v-on:service-log:deleted="deleteServiceLog">
          </history-repeatable>
        </div>
        <v-flex xs12>
          <v-alert :value="errorMsg" type="error">
            {{errorMsg}}
          </v-alert>
        </v-flex>
        <v-flex
          v-if="serviceLogsArray.length"
          class="text-xs-center pa-0">
          <v-pagination
            class="pagination"
            v-model="pageNumber"
            :length="totalPages"
            @next="retrieveLogs"
            @previous="retrieveLogs"
            @input="retrieveLogs">
          </v-pagination>
        </v-flex>
        <v-flex v-if="serviceLogsArray.length === 0 && !errorMsg">
          <v-flex class="font-italic">
            Optionally filter by provider, student, location, start date, and/or end date, then click on <b>"Retrieve Logs"</b>
          </v-flex>
        </v-flex>
      </v-layout>
      <bottom-nav></bottom-nav>
    </v-container>
    <full-screen-loading-spinner v-if="showLoading"></full-screen-loading-spinner>
  </div>
</template>

<script>
import * as moment from 'moment';
import Toolbar from '@/components/toolbar';
import api from '@/utils/api';
import utilityFunctions from '@/utils/functions';
import FullScreenLoadingSpinner from '@/components/full-screen-loading-spinner';
import BottomNav from '@/components/bottom-nav';
import HistoryRepeatable from '@/components/service-log/history/history-repeatable';
import * as Constants from '@/constants';

export default {
  name: 'SlSearch',
  components: {
    Toolbar,
    FullScreenLoadingSpinner,
    HistoryRepeatable,
    BottomNav
  },
  computed: {
    isMobile() {
      return this.$mq === 'mobile';
    },
    isTablet() {
      return this.$mq === 'tablet';
    },
    isDesktop() {
      return this.$mq === 'laptop' || this.$mq === 'desktop';
    }
  },
  data() {
    return {
      providersArray: null,
      provider: null,
      selectedStudent: null,
      // start date
      serviceMenuStart: false,
      serviceDateStart: null,
      dateStart: null,
      // end date
      serviceMenuEnd: false,
      serviceDateEnd: null,
      dateEnd: null,
      // location
      selectedLocation: null,
      serviceLogsArray: [],
      errorMsg: null,
      showLoading: false,
      pageNumber: 1,
      itemsPerPageArray: [10, 25, 50, 100],
      itemsPerPage: 50,
      loadData: false,
      totalCount: null,
      totalPages: null,
      // service location autocomplete
      locationSearchError: null,
      locationSearchModel: '',
      locationSearchLoading: false,
      locationSearchInput: '',
      locationSearchArray: [],
      // find student autocomplete
      studentSearchError: null,
      studentSearchModel: '',
      studentSearchLoading: false,
      studentSearchInput: '',
      studentSearchArray: [],
      // provider autocomplete
      providerSearchError: '',
      providerSearchModel: '',
      providerSearchInput: null,
      providerSearchLoading: false,
      providerSearchArray: []
    };
  },
  mounted() {
    // if there are stored query params, reexecute that search
    if (this.$route && this.$route.query && !this.isQueryParamsEmpty(this.$route.query)) {
      const query = this.$route.query;
      this.dateStart = query.from ? moment(query.from, 'YYYY-MM-DD').format('MM-DD-YYYY') : '';
      this.dateEnd = query.to ? moment(query.to, 'YYYY-MM-DD').format('MM-DD-YYYY') : '';
      this.providerSearchModel = JSON.parse(localStorage.getItem('provider-search-object')) || '';
      this.studentSearchModel = JSON.parse(localStorage.getItem('student-search-object')) || '';
      this.locationSearchModel = JSON.parse(localStorage.getItem('location-search-object')) || '';
      this.pageNumber = query.pagenumber ? Number(query.pagenumber) : 1;
      this.itemsPerPage = query.itemsperpage ? Number(query.itemsperpage) : 50;
      this.retrieveLogs();
    }
  },
  methods: {
    // loops over all of the values in the route query object and checks to see if its empty
    isQueryParamsEmpty(obj) {
      let bool = true;
      // set an array of object values
      const objectValues = Object.values(obj);
      // loop over object values and make sure none of them are null
      for (let i = 0; i < objectValues.length; i++) {
        if (objectValues[i] !== null || objectValues[i] !== '') {
          bool = false;
          break;
        }
      }
      return bool;
    },
    clearSearch() {
      this.dateStart = null;
      this.dateEnd = null;
      this.providerSearchModel = null;
      this.studentSearchModel = null;
      this.locationSearchModel = null;
    },
    clearProviderAutoComplete() {
      if (!this.providerSearchModel) {
        this.providerSearchArray = [];
      }
    },
    clearStudentAutoComplete() {
      if (!this.studentSearchModel) {
        this.studentSearchArray = [];
      }
    },
    clearLocationAutoComplete() {
      if (!this.locationSearchModel) {
        this.locationSearchArray = [];
      }
    },
    serviceDateStartChanged(val) {
      this.dateStart = utilityFunctions.formatDate(val);
    },
    serviceEndDateChanged(val) {
      this.dateEnd = utilityFunctions.formatDate(val);
    },
    formatDateForSearch(date) {
      if (!date) return '';
      return moment(date, 'MM-DD-YYYY').format('YYYY-MM-DD');
    },
    retrieveLogs() {
      let url = Constants.PTSL_URL();
      let from = '';
      let to = '';
      let provideruserid = '';
      let studentid = '';
      let locationid = '';
      let pagenumber = '';
      let itemsperpage = '';
      let str = '?';
      let paramCount = 0;

      if (this.dateStart) {
        paramCount += 1;
        from = this.formatDateForSearch(this.dateStart);
        str += `from=${from}`;
      }

      if (this.dateEnd) {
        if (paramCount > 0) str += '&';
        paramCount += 1;
        to = this.formatDateForSearch(this.dateEnd);
        str += `to=${to}`;
      }

      if (this.providerSearchModel) {
        // set the object in local storage to use when navigating back to the page
        localStorage.setItem('provider-search-object', JSON.stringify(this.providerSearchModel));
        if (paramCount > 0) str += '&';
        paramCount += 1;
        provideruserid = this.providerSearchModel.userId;
        str += `provideruserid=${provideruserid}`;
      } else {
        // parameter wasn't used make sure nothing exists in local storage
        localStorage.removeItem('provider-search-object');
      }

      if (this.studentSearchModel) {
        // set the object in local storage to use when navigating back to the page
        localStorage.setItem('student-search-object', JSON.stringify(this.studentSearchModel));
        if (paramCount > 0) str += '&';
        paramCount += 1;
        studentid = this.studentSearchModel.studentId;
        str += `studentid=${studentid}`;
      } else {
        // parameter wasn't used make sure nothing exists in local storage
        localStorage.removeItem('student-search-object');
      }

      if (this.locationSearchModel) {
        // set the object in local storage to use when navigating back to the page
        localStorage.setItem('location-search-object', JSON.stringify(this.locationSearchModel));
        if (paramCount > 0) str += '&';
        paramCount += 1;
        locationid = this.locationSearchModel.locationId;
        str += `locationid=${locationid}`;
      } else {
        // parameter wasn't used make sure nothing exists in local storage
        localStorage.removeItem('location-search-object');
      }

      if (this.pageNumber) {
        if (paramCount > 0) str += '&';
        paramCount += 1;
        pagenumber = this.pageNumber;
        str += `pagenumber=${pagenumber}`;
      }

      if (this.itemsPerPage) {
        if (paramCount > 0) str += '&';
        itemsperpage = this.itemsPerPage;
        str += `itemsperpage=${itemsperpage}`;
      }

      // set route params
      this.$router.push({
        path: this.$route.path,
        query: {
          from,
          to,
          provideruserid,
          studentid,
          locationid,
          pagenumber,
          itemsperpage
        }
      });
      url += str;
      this.executeSearch(url);
    },
    executeSearch(url) {
      this.errorMsg = null;
      this.showLoading = true;
      this.serviceLogsArray = [];
      api.get(url)
        .then((res) => {
          const data = res.data.data;
          this.totalCount = data.totalCount;
          this.totalPages = data.totalPages;
          if (!data.items.length) throw new Error('No Service Logs found.')
          this.serviceLogsArray = data.items;
        })
        .catch((error) => {
          this.errorMsg = error.message;
        })
        .finally(() => {
          this.showLoading = false;
        });
    },
    onRetrieveLogsClicked() {
      // reset items per page
      this.itemsPerPage = 50;
      // reset pageNumber
      this.pageNumber = 1;
      this.retrieveLogs();
    },
    deleteServiceLog() {
      // refresh the data
      this.retrieveLogs();
    }
  },
  created() {
    this.provider = this.selectedProvider;
    Promise.all([
      api.get(Constants.GET_SCHOOL_LIST_URL()),
      api.get(Constants.PTSL_PROVIDER_LIST_URL('', ''))
    ])
      .then((promises) => {
        this.schoolArray = promises[0].data.data;
        this.providersArray = promises[1].data.data;
      })
      .catch((error) => {
        this.errorMsg = error.message;
      });
  },
  watch: {
    // fires the api call to get service location list
    locationSearchInput() {
      // show loading
      this.locationSearchLoading = true;
      // clear timeout if function is called before setTimeout fires
      clearTimeout(this.timeout);
      // make sure the user types something before firing request
      if (this.locationSearchInput) {
        // fire the request after a delay
        this.timeout = setTimeout(() => {
          api.get(Constants.GET_SERVICE_LOCATIONS_URL(this.locationSearchInput))
            .then((res) => {
              this.locationSearchArray = res.data.data;
            })
            .catch((error) => {
              this.locationSearchError = error.message;
            })
            .finally(() => {
              this.locationSearchLoading = false;
            });
        }, 300);
      } else {
        this.locationSearchLoading = false;
      }
    },
    // fires the api call to get service location list
    studentSearchInput() {
      // show loading
      this.studentSearchLoading = true;
      // clear timeout if function is called before setTimeout fires
      clearTimeout(this.timeout);
      // make sure the user types something before firing request
      if (this.studentSearchInput) {
        // fire the request after a delay
        this.timeout = setTimeout(() => {
          api.get(Constants.PTSL_GET_STUDENT_LIST_FOR_DDL_URL(this.studentSearchInput))
            .then((res) => {
              this.studentSearchArray = res.data.data;
            })
            .catch((error) => {
              this.studentSearchError = error.message;
            })
            .finally(() => {
              this.studentSearchLoading = false;
            });
        }, 300);
      } else {
        this.studentSearchLoading = false;
      }
    },
    providerSearchInput() {
      // show loading
      this.providerSearchLoading = true;
      // clear timeout if function is called before setTimeout fires
      clearTimeout(this.timeout);
      // make sure the user types something before firing request
      if (this.providerSearchInput) {
        // fire the request after a delay
        this.timeout = setTimeout(() => {
          api.get(Constants.PTSL_PROVIDER_LIST_URL(this.providerSearchInput, ''))
            .then((res) => {
              this.providerSearchArray = res.data.data;
            })
            .catch((error) => {
              this.providerSearchError = error.message;
            })
            .finally(() => {
              // stop loading
              this.providerSearchLoading = false;
            });
        }, 300);
      } else {
        this.providerSearchLoading = false;
      }
    },
    providerSearchModel(newVal, oldVal) {
      if (!newVal && oldVal) this.providerSearchArray = [];
    },
    studentSearchModel(newVal, oldVal) {
      if (!newVal && oldVal) this.studentSearchArray = [];
    },
    locationSearchModel(newVal, oldVal) {
      if (!newVal && oldVal) this.locationSearchArray = [];
    }
  }
};
</script>

<style lang="scss" scoped>
  .clear-parameters-button {
    color: var(--v-primary-base);
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.15) !important;
  }

  .service-logs-toolbar {
    border-radius: 10px;
    margin: 16px 0 8px 0 !important;

    .items-per-page-dropdown {
      position: relative;
      background-color: #fff;
      color: var(--v-primary-base);
      font-weight: bold;
      width: 50px;
      float: left;
      max-width: 100%;
      border-radius: 2px;
      select {
        font-size: 1rem;
        width: 100%;
        padding: 5px;
        border: none;
        background-color: transparent;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        &:active,
        &:focus {
          outline: none;
          box-shadow: none;
        }
      }
      &:after {
        content: " ";
        position: absolute;
        top: 50%;
        margin-top: -2px;
        right: 8px;
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 5px solid #aaa;
      }
    }
  }

  /deep/ .pagination {
    .v-pagination__navigation {
      box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
    }
    .v-pagination__item {
      box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
      color: var(--v-primary-base);
      &.v-pagination__item--active {
        box-shadow: 0 0 8px rgba(32, 41, 89, 0.8);
        color: #fff;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/views/service-log/service-log-search.vue